import {
  ActionError,
  Button,
  Chip,
  ChipAdd,
  ConfirmDialog,
  Field,
  FieldGrid,
  FileButton,
  Input,
  ListRow,
  ListRows,
  PhotoSlot,
  Select,
  Surface,
  toast,
} from '@westhetics/ui'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { isApiError } from '@/lib/api'
import { brDateToIso, isoToBrDate, maskDate, todayIso } from '@/lib/dates'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import {
  slotKey,
  useArchiveLotPhoto,
  useDeleteProduct,
  useLabelReading,
  useProductSearch,
  useRestoreLotPhoto,
  useRestoreProduct,
  useUpdateProduct,
  useUploadLotPhoto,
  type LabelReading,
  type LotSlot,
  type Product,
  type ProductInput,
  type ProductUnit,
  type TreatmentPlan,
} from './api'
import {
  MSG_LABEL_READING_UNAVAILABLE,
  PRODUCT_FOOTER,
  REGIONS_HINT,
  SLOTS,
  SLOT_LABELS,
  UNITS,
  UNIT_LABELS,
  parseQuantity,
  productName,
  productTitle,
  quantityText,
} from './labels'
import { PHOTO_ACCEPT } from './photo-file'
import { usePhotoCapture } from './use-photo-capture'

/**
 * Um produto aplicado (docs/areas/planos-de-tratamento.md, "Aba Produtos"; RN-PLA-07 a 14):
 * busca única de produto, data, medida e unidade, regiões em chips, lote, validade, registro
 * Anvisa e os três slots de foto do lote. Cada campo grava ao sair (`PUT` com a versão do produto);
 * regiões e fotos gravam na hora. "pendente" em plum enquanto falta lote, validade ou foto.
 */

interface Draft {
  name: string
  manufacturer: string
  applicationDate: string
  quantity: string
  unit: ProductUnit | ''
  lotNumber: string
  expiryDate: string
  anvisaRegistration: string
}

type DraftKey = keyof Draft

function draftFrom(product: Product): Draft {
  return {
    name: product.name ?? '',
    manufacturer: product.manufacturer ?? '',
    applicationDate: isoToBrDate(product.applicationDate),
    quantity: quantityText(product.quantity),
    unit: product.unit ?? '',
    lotNumber: product.lotNumber ?? '',
    expiryDate: isoToBrDate(product.expiryDate),
    anvisaRegistration: product.anvisaRegistration ?? '',
  }
}

const MSG_DATE = 'Use o formato dd/mm/aaaa.'
const MSG_QUANTITY = 'Informe a medida aplicada, como 1,0.'
const MSG_ANVISA = 'O registro Anvisa tem entre 4 e 20 caracteres.'

/** O que vai no `PUT` para um campo, ou a causa de não ir ainda. */
function inputFor(key: DraftKey, draft: Draft): { input?: ProductInput; error?: string } {
  const value = draft[key].trim()
  switch (key) {
    case 'applicationDate':
    case 'expiryDate': {
      if (value === '') return { input: { [key]: null } }
      const iso = brDateToIso(value)
      return iso ? { input: { [key]: iso } } : { error: MSG_DATE }
    }
    case 'quantity': {
      if (value === '') return { input: { quantity: null } }
      const quantity = parseQuantity(value)
      return quantity === null ? { error: MSG_QUANTITY } : { input: { quantity } }
    }
    case 'unit':
      return { input: { unit: value ? (value as ProductUnit) : null } }
    case 'anvisaRegistration':
      if (value === '') return { input: { anvisaRegistration: null } }
      return /^[\d.]{4,20}$/.test(value)
        ? { input: { anvisaRegistration: value } }
        : { error: MSG_ANVISA }
    default:
      return { input: { [key]: value || null } }
  }
}

function currentValue(key: DraftKey, product: Product): string {
  return draftFrom(product)[key]
}

export function ProductCard({
  plan,
  product,
  readOnly,
  errors = {},
  highlighted,
  onOpenQr,
}: {
  plan: TreatmentPlan
  product: Product
  readOnly: boolean
  /** O que o encerramento recusou neste produto (`products[0].lotNumber`), por campo. */
  errors?: Record<string, string>
  /** O cartão que o 422 do encerramento apontou: rola até ele. */
  highlighted?: boolean
  onOpenQr: (product: Product) => void
}) {
  const id = useId()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<Draft>(() => draftFrom(product))
  const [localErrors, setLocalErrors] = useState<Partial<Record<DraftKey, string>>>({})
  const [refused, setRefused] = useState<Partial<Record<string, string>>>({})
  const [removing, setRemoving] = useState(false)
  const [reading, setReading] = useState<LabelReading | null>(null)
  const [readingError, setReadingError] = useState<string | null>(null)
  const [slotBusy, setSlotBusy] = useState<Partial<Record<LotSlot, string>>>({})
  const [slotError, setSlotError] = useState<Partial<Record<LotSlot, string>>>({})
  const [replaceAsk, setReplaceAsk] = useState<{ key: DraftKey; value: string }[] | null>(null)
  const card = useRef<HTMLElement>(null)
  const update = useUpdateProduct(plan.id)
  const remove = useDeleteProduct(plan.id)
  const restore = useRestoreProduct(plan.id)
  const uploadLot = useUploadLotPhoto(plan.id)
  const archiveLot = useArchiveLotPhoto(plan.id)
  const restoreLot = useRestoreLotPhoto(plan.id)
  const labelReading = useLabelReading(plan.id)
  const capture = usePhotoCapture()

  useEffect(() => {
    if (highlighted && typeof card.current?.scrollIntoView === 'function') {
      card.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [highlighted])

  const field = (key: DraftKey) => localErrors[key] ?? refused[key] ?? errors[key] ?? null

  function save(input: ProductInput, keys: DraftKey[]) {
    update.mutate(
      { productId: product.id, version: product.version, input },
      {
        onSuccess: () => {
          setRefused((current) => {
            const next = { ...current }
            for (const key of keys) delete next[key]
            return next
          })
        },
        onError: (error) => {
          const fields = fieldErrors(error)
          if (Object.keys(fields).length > 0) setRefused((current) => ({ ...current, ...fields }))
          else toast(errorMessage(error))
        },
      },
    )
  }

  /** Ao sair do campo: valida e grava se mudou (RN-PLA-08, RN-PLA-24). */
  function commit(key: DraftKey) {
    const { input, error } = inputFor(key, draft)
    setLocalErrors((current) => ({ ...current, [key]: error }))
    if (error || !input) return
    if (draft[key].trim() === currentValue(key, product).trim()) return
    save(input, [key])
  }

  function change(key: DraftKey, value: string) {
    setDraft((current) => ({ ...current, [key]: value }))
    setLocalErrors((current) => ({ ...current, [key]: undefined }))
  }

  function choose(item: {
    catalogProductId: string | null
    name: string
    manufacturer: string
    unit: ProductUnit | null
  }) {
    setDraft((current) => ({
      ...current,
      name: item.name,
      manufacturer: item.manufacturer,
      unit: item.unit ?? current.unit,
    }))
    save(
      {
        catalogProductId: item.catalogProductId,
        name: item.name,
        manufacturer: item.manufacturer,
        ...(item.unit ? { unit: item.unit } : {}),
      },
      ['name', 'manufacturer', 'unit'],
    )
  }

  function useSuggestedDate() {
    const suggested = plan.procedureDate ?? todayIso()
    change('applicationDate', isoToBrDate(suggested))
    save({ applicationDate: suggested }, ['applicationDate'])
  }

  function removeRegion(index: number) {
    const regions = product.regions
      .filter((_, i) => i !== index)
      .map((r) => (r.regionId ? { regionId: r.regionId } : { name: r.name }))
    save({ regions }, [])
  }

  function askRemove() {
    const hasPhoto = SLOTS.some((slot) => product.lotPhotos[slotKey(slot)])
    if (hasPhoto) {
      setRemoving(true)
      return
    }
    doRemove()
  }

  function doRemove() {
    remove.mutate(
      { productId: product.id, version: product.version },
      {
        onSuccess: () => {
          setRemoving(false)
          toast(`${productTitle(product.position)} arquivado`, {
            undo: () => {
              restore.mutate(product.id, {
                onSuccess: () => toast(`${productTitle(product.position)} de volta ao plano.`),
                onError: (error) => toast(errorMessage(error)),
              })
            },
          })
        },
        onError: (error) => {
          setRemoving(false)
          toast(errorMessage(error))
        },
      },
    )
  }

  async function sendToSlot(slot: LotSlot, files: File[], source: 'desktop' | 'gallery') {
    setSlotError((current) => ({ ...current, [slot]: undefined }))
    await capture(
      files.slice(0, 1),
      async (photo) => {
        setSlotBusy((current) => ({ ...current, [slot]: 'Enviando…' }))
        try {
          await uploadLot.mutateAsync({ productId: product.id, slot, photo, source })
        } catch (error) {
          const fields = fieldErrors(error)
          setSlotError((current) => ({
            ...current,
            [slot]: fields.file ?? fields.sizeBytes ?? fields.mimeType ?? errorMessage(error),
          }))
        } finally {
          setSlotBusy((current) => ({ ...current, [slot]: undefined }))
        }
      },
      (message) => setSlotError((current) => ({ ...current, [slot]: message })),
    )
  }

  function archiveSlot(slot: LotSlot) {
    const photo = product.lotPhotos[slotKey(slot)]
    if (!photo) return
    archiveLot.mutate(
      { productId: product.id, slot, photoId: photo.id },
      {
        onSuccess: () =>
          toast('Foto arquivada', {
            undo: () =>
              restoreLot.mutate(
                { productId: product.id, photoId: photo.id },
                { onError: (error) => toast(errorMessage(error)) },
              ),
          }),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function readLabel() {
    setReadingError(null)
    labelReading.mutate(product.id, {
      onSuccess: setReading,
      onError: (error) =>
        setReadingError(
          isApiError(error) && error.code === 'unavailable'
            ? MSG_LABEL_READING_UNAVAILABLE
            : errorMessage(error),
        ),
    })
  }

  const SUGGESTION_KEYS: DraftKey[] = [
    'name',
    'manufacturer',
    'lotNumber',
    'expiryDate',
    'anvisaRegistration',
  ]
  const suggestionFor = (key: DraftKey): string | null => {
    if (!reading) return null
    const raw = reading.suggestions[key as keyof LabelReading['suggestions']]
    if (!raw) return null
    const value = key === 'expiryDate' ? isoToBrDate(raw) || raw : raw
    return value.trim() === draft[key].trim() ? null : value
  }

  function applySuggestions(entries: { key: DraftKey; value: string }[]) {
    if (entries.length === 0) return
    const next: Draft = { ...draft }
    const input: ProductInput = {}
    for (const { key, value } of entries) {
      // As sugestões só tocam campos de texto; a unidade não vem da etiqueta.
      if (key === 'unit') continue
      next[key] = value
      const result = inputFor(key, next)
      if (result.input) Object.assign(input, result.input)
    }
    setDraft(next)
    save(
      input,
      entries.map((e) => e.key),
    )
  }

  function applySuggestion(key: DraftKey) {
    const value = suggestionFor(key)
    if (value === null) return
    if (draft[key].trim() !== '') setReplaceAsk([{ key, value }])
    else applySuggestions([{ key, value }])
  }

  function useAllSuggestions() {
    const entries = SUGGESTION_KEYS.map((key) => ({ key, value: suggestionFor(key) })).filter(
      (e): e is { key: DraftKey; value: string } => e.value !== null,
    )
    const empty = entries.filter((e) => draft[e.key].trim() === '')
    const filled = entries.filter((e) => draft[e.key].trim() !== '')
    applySuggestions(empty)
    if (filled.length > 0) setReplaceAsk(filled)
  }

  const suggestionHint = (key: DraftKey) => {
    const value = suggestionFor(key)
    if (value === null || readOnly) return undefined
    return (
      <span>
        Lido na foto: {value} ·{' '}
        <button
          type="button"
          className="text-ink cursor-pointer underline"
          onClick={() => applySuggestion(key)}
        >
          Usar
        </button>
      </span>
    )
  }

  const hasLotPhoto = SLOTS.some((slot) => product.lotPhotos[slotKey(slot)])
  const title = productTitle(product.position)
  const labelKeys: Record<DraftKey, string> = {
    name: 'Produto',
    manufacturer: 'Fabricante',
    applicationDate: 'Data da aplicação',
    quantity: 'Medida aplicada',
    unit: 'Unidade',
    lotNumber: 'Lote',
    expiryDate: 'Validade',
    anvisaRegistration: 'Registro Anvisa',
  }

  return (
    <section ref={card} aria-label={title} className="scroll-mt-20">
      <Surface className="max-desk:px-4 flex flex-col gap-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <h3 className="font-display text-lead text-ink leading-[1.3] font-normal">{title}</h3>
            {product.isLotPending && plan.status === 'open' ? (
              <span className="font-body text-eyebrow text-plum font-light tracking-normal">
                lote pendente
              </span>
            ) : null}
            {product.isExpiredAtApplication ? (
              <span className="font-body text-eyebrow text-plum font-light tracking-normal">
                Vencido na aplicação
              </span>
            ) : null}
          </div>
          {!readOnly ? (
            <div className="flex items-center gap-4">
              {plan.labelReadingAvailable && hasLotPhoto ? (
                <Button
                  variant="text"
                  size="sm"
                  loading={labelReading.isPending}
                  loadingLabel="Lendo a etiqueta…"
                  onClick={readLabel}
                >
                  Preencher pela foto
                </Button>
              ) : null}
              <Button
                variant="text"
                size="sm"
                loading={remove.isPending}
                loadingLabel="Arquivando…"
                onClick={askRemove}
              >
                excluir
              </Button>
            </div>
          ) : null}
        </div>

        {readingError ? (
          <ActionError
            title="Não foi possível ler a etiqueta"
            description={readingError}
            onDismiss={() => setReadingError(null)}
          />
        ) : null}
        {reading ? (
          <ActionError
            title={`Sugestões lidas na foto · confiança ${reading.confidence === 'high' ? 'alta' : reading.confidence === 'medium' ? 'média' : 'baixa'}`}
            description="Cada sugestão aparece ao lado do campo. Campo já preenchido só muda com a sua confirmação."
            actions={
              <>
                <Button variant="outline" size="sm" onClick={useAllSuggestions}>
                  Usar todas
                </Button>
                <Button variant="text" size="sm" onClick={() => setReading(null)}>
                  Dispensar
                </Button>
              </>
            }
          />
        ) : null}

        <ProductSearchField
          id={`${id}-name`}
          value={draft.name}
          readOnly={readOnly}
          error={field('name')}
          hint={suggestionHint('name')}
          onChange={(value) => change('name', value)}
          onBlur={() => commit('name')}
          onChoose={choose}
        />

        <FieldGrid>
          <Field
            id={`${id}-manufacturer`}
            label={labelKeys.manufacturer}
            error={field('manufacturer')}
            hint={suggestionHint('manufacturer')}
          >
            <Input
              value={draft.manufacturer}
              readOnly={readOnly}
              onChange={(e) => change('manufacturer', e.target.value)}
              onBlur={() => commit('manufacturer')}
            />
          </Field>
          <Field
            id={`${id}-date`}
            label={labelKeys.applicationDate}
            error={field('applicationDate')}
            hint={
              !readOnly && draft.applicationDate === '' ? (
                <span>
                  Sugestão: {isoToBrDate(plan.procedureDate ?? todayIso())}{' '}
                  {plan.procedureDate ? '(data do procedimento)' : '(hoje)'} ·{' '}
                  <button
                    type="button"
                    className="text-ink cursor-pointer underline"
                    onClick={useSuggestedDate}
                  >
                    usar
                  </button>
                </span>
              ) : undefined
            }
          >
            <Input
              inputMode="numeric"
              placeholder="dd/mm/aaaa"
              value={draft.applicationDate}
              readOnly={readOnly}
              onChange={(e) => change('applicationDate', maskDate(e.target.value))}
              onBlur={() => commit('applicationDate')}
            />
          </Field>
          <Field
            id={`${id}-quantity`}
            label={labelKeys.quantity}
            error={field('quantity')}
            hint="Total por produto, não por região. Até 3 casas decimais."
          >
            <Input
              inputMode="decimal"
              placeholder="1,0"
              value={draft.quantity}
              readOnly={readOnly}
              onChange={(e) => change('quantity', e.target.value.replace(/[^\d.,]/g, ''))}
              onBlur={() => commit('quantity')}
            />
          </Field>
          <Field id={`${id}-unit`} label={labelKeys.unit} error={field('unit')}>
            <Select
              value={draft.unit}
              placeholder="Selecione"
              disabled={readOnly}
              onChange={(e) => {
                change('unit', e.target.value)
                const next = e.target.value as ProductUnit | ''
                if (next !== (product.unit ?? '')) save({ unit: next ? next : null }, ['unit'])
              }}
            >
              {UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {UNIT_LABELS[unit]}
                </option>
              ))}
            </Select>
          </Field>
        </FieldGrid>

        <div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="eyebrow">Regiões de aplicação</span>
            <span className="font-body text-eyebrow text-label-soft font-light tracking-normal">
              {product.regions.length} de 20
            </span>
          </div>
          <div className="mt-[6px] flex flex-wrap items-center gap-2">
            {product.regions.map((region, index) => (
              <Chip
                key={`${region.regionId ?? region.name}-${index}`}
                onRemove={readOnly ? undefined : () => removeRegion(index)}
              >
                {region.label}
              </Chip>
            ))}
            {!readOnly ? (
              <ChipAdd onClick={() => navigate(routes.treatmentPlanRegions(plan.id, product.id))}>
                + Adicionar região
              </ChipAdd>
            ) : null}
          </div>
          {errors.regions ? (
            <p
              role="alert"
              className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
            >
              {errors.regions}
            </p>
          ) : (
            <p className="font-body text-eyebrow text-label mt-[6px] leading-[1.5] font-light tracking-normal">
              {REGIONS_HINT}
            </p>
          )}
        </div>

        <div className="border-hairline border-t pt-5">
          <h4 className="eyebrow">Lote e validade · fotos obrigatórias</h4>
          <FieldGrid className="mt-4">
            <Field
              id={`${id}-lot`}
              label={labelKeys.lotNumber}
              error={field('lotNumber')}
              hint={suggestionHint('lotNumber') ?? 'Como impresso na etiqueta.'}
            >
              <Input
                value={draft.lotNumber}
                readOnly={readOnly}
                onChange={(e) => change('lotNumber', e.target.value.slice(0, 40))}
                onBlur={() => commit('lotNumber')}
              />
            </Field>
            <Field
              id={`${id}-expiry`}
              label={labelKeys.expiryDate}
              error={field('expiryDate')}
              hint={suggestionHint('expiryDate') ?? 'Como está na embalagem.'}
            >
              <Input
                inputMode="numeric"
                placeholder="dd/mm/aaaa"
                value={draft.expiryDate}
                readOnly={readOnly}
                onChange={(e) => change('expiryDate', maskDate(e.target.value))}
                onBlur={() => commit('expiryDate')}
              />
            </Field>
            <Field
              id={`${id}-anvisa`}
              label={labelKeys.anvisaRegistration}
              optional
              error={field('anvisaRegistration')}
              hint={suggestionHint('anvisaRegistration')}
            >
              <Input
                inputMode="numeric"
                value={draft.anvisaRegistration}
                readOnly={readOnly}
                onChange={(e) =>
                  change('anvisaRegistration', e.target.value.replace(/[^\d.]/g, '').slice(0, 20))
                }
                onBlur={() => commit('anvisaRegistration')}
              />
            </Field>
          </FieldGrid>
          <div className="tablet:grid-cols-3 mt-5 grid grid-cols-1 gap-4">
            {SLOTS.map((slot) => {
              const photo = product.lotPhotos[slotKey(slot)]
              const slotErrorKey = `lotPhotos.${slotKey(slot)}`
              return (
                <PhotoSlot
                  key={slot}
                  label={SLOT_LABELS[slot]}
                  pending={plan.status === 'open'}
                  photo={
                    photo
                      ? {
                          src: photo.url,
                          alt: `Foto do ${SLOT_LABELS[slot].toLocaleLowerCase('pt-BR')} · ${productName(product)}`,
                          href: photo.url ?? undefined,
                        }
                      : null
                  }
                  busyLabel={slotBusy[slot] ?? null}
                  error={slotError[slot] ?? errors[slotErrorKey] ?? null}
                  actions={
                    readOnly ? undefined : (
                      <>
                        <FileButton
                          variant="text"
                          size="sm"
                          accept={PHOTO_ACCEPT}
                          capture="environment"
                          onFiles={(files) => void sendToSlot(slot, files, 'desktop')}
                        >
                          {photo ? 'Trocar · tirar foto' : 'Tirar foto'}
                        </FileButton>
                        <FileButton
                          variant="text"
                          size="sm"
                          accept={PHOTO_ACCEPT}
                          onFiles={(files) => void sendToSlot(slot, files, 'gallery')}
                        >
                          Galeria
                        </FileButton>
                        <Button variant="text" size="sm" onClick={() => onOpenQr(product)}>
                          Usar celular (QR)
                        </Button>
                        {photo ? (
                          <Button variant="text" size="sm" onClick={() => archiveSlot(slot)}>
                            arquivar
                          </Button>
                        ) : null}
                      </>
                    )
                  }
                />
              )
            })}
          </div>
        </div>

        <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
          {PRODUCT_FOOTER}
        </p>

        <ConfirmDialog
          open={removing}
          onOpenChange={(open) => {
            if (!remove.isPending) setRemoving(open)
          }}
          tone="primary"
          title={`Excluir "${productName(product)}"?`}
          effect={`O produto e as ${SLOTS.filter((slot) => product.lotPhotos[slotKey(slot)]).length === 1 ? '1 foto' : `${SLOTS.filter((slot) => product.lotPhotos[slotKey(slot)]).length} fotos`} do lote saem deste plano e do PDF. Nada é apagado: ficam na trilha da clínica e podem voltar.`}
          confirmLabel="Arquivar"
          loading={remove.isPending}
          loadingLabel="Arquivando…"
          onConfirm={doRemove}
        />

        <ConfirmDialog
          open={replaceAsk !== null}
          onOpenChange={(open) => {
            if (!open) setReplaceAsk(null)
          }}
          tone="primary"
          title="Trocar o que está digitado pelo lido na foto?"
          effect={
            replaceAsk
              ? replaceAsk
                  .map((e) => `${labelKeys[e.key]}: "${draft[e.key]}" vira "${e.value}".`)
                  .join(' ')
              : ''
          }
          confirmLabel="Trocar"
          onConfirm={() => {
            if (replaceAsk) applySuggestions(replaceAsk)
            setReplaceAsk(null)
          }}
        />
      </Surface>
    </section>
  )
}

/** A busca única "Buscar produto ou fabricante" (P-PLA-02): últimos usados no topo, texto livre vale. */
function ProductSearchField({
  id,
  value,
  readOnly,
  error,
  hint,
  onChange,
  onBlur,
  onChoose,
}: {
  id: string
  value: string
  readOnly: boolean
  error: string | null
  hint?: React.ReactNode
  onChange: (value: string) => void
  onBlur: () => void
  onChoose: (item: {
    catalogProductId: string | null
    name: string
    manufacturer: string
    unit: ProductUnit | null
  }) => void
}) {
  const [focused, setFocused] = useState(false)
  const term = useDebounced(value.trim())
  const search = useProductSearch(term, focused && !readOnly)
  const results = search.data
  const showList =
    focused &&
    !readOnly &&
    results &&
    (results.recentlyUsed.length > 0 || results.catalog.length > 0)
  return (
    <div className="relative">
      <Field
        id={id}
        label="Produto"
        error={error}
        hint={
          hint ?? 'Buscar produto ou fabricante. Se não achar, escreva o nome: vale do mesmo jeito.'
        }
      >
        <Input
          value={value}
          readOnly={readOnly}
          placeholder="Buscar produto ou fabricante"
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Dá tempo de o clique na lista acontecer antes de a lista sumir.
            window.setTimeout(() => setFocused(false), 150)
            onBlur()
          }}
        />
      </Field>
      {showList ? (
        <div className="shadow-float absolute right-0 left-0 z-10 mt-1">
          <ListRows
            role="listbox"
            aria-label="Produtos encontrados"
            className="max-h-[320px] overflow-y-auto"
          >
            {results.recentlyUsed.length > 0 ? (
              <div className="border-hairline bg-pampas eyebrow border-b px-4 py-[10px]">
                Últimos usados
              </div>
            ) : null}
            {results.recentlyUsed.map((item) => (
              <ListRow
                key={`recent-${item.name}-${item.manufacturer}`}
                size="lead"
                title={item.name}
                meta={item.manufacturer}
                aria-label={`Escolher ${item.name}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChoose(item)
                  setFocused(false)
                }}
              />
            ))}
            {results.catalog.length > 0 ? (
              <div className="border-hairline bg-pampas eyebrow border-y px-4 py-[10px]">
                Catálogo
              </div>
            ) : null}
            {results.catalog.map((item) => (
              <ListRow
                key={`catalog-${item.catalogProductId}`}
                size="lead"
                title={item.name}
                meta={item.manufacturer}
                aria-label={`Escolher ${item.name}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChoose(item)
                  setFocused(false)
                }}
              />
            ))}
          </ListRows>
        </div>
      ) : null}
    </div>
  )
}
